import { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { SegBadge } from "./OpeningBalancesPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useAppCtx } from "../contexts/AppContext";
import { PeriodFilter, periodParams } from "../components/PeriodFilter";
import { PartnerAutocomplete } from "../components/PartnerAutocomplete";
import { Upload, Trash2, FileUp, FileText, AlertTriangle, CheckCircle2, RefreshCw, Search, FilePlus2 } from "lucide-react";
import { toast } from "sonner";

function num(n, d = 2) {
    return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}
function fmtVol(n, segment) {
    if (n === null || n === undefined) return "—";
    if (segment === "Voice") return `${num(n, 2)} min`;
    return num(n, 0);
}

export default function PurchaseInvoicesPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("purchase_invoices", "write");
    const { entities, selectedSegment } = useAppCtx();
    const [period, setPeriod] = useState({ from: null, to: null, preset: "this_month" });
    const [cycles, setCycles] = useState([]);
    const [skippedCycles, setSkippedCycles] = useState([]);
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("recon");
    const [query, setQuery] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = { ...periodParams(period) };
            if (selectedSegment !== "all") params.segment = selectedSegment;
            const [active, skipped, u] = await Promise.all([
                api.get("/purchase/cycles", { params }),
                api.get("/purchase/cycles", { params: { ...params, only_skipped: true } }),
                api.get("/purchase/daily-uploads", { params }),
            ]);
            setCycles(active.data || []);
            setSkippedCycles(skipped.data || []);
            setUploads(u.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedSegment, period]);

    // Client-side search by partner name (case-insensitive substring).
    // Mirrors the search UX on the Sales Reconciliation tab.
    const filterByQuery = (rows) => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => (r.partner_name || "").toLowerCase().includes(q));
    };
    const filteredCycles = useMemo(() => filterByQuery(cycles), [cycles, query]);
    const filteredSkipped = useMemo(() => filterByQuery(skippedCycles), [skippedCycles, query]);

    // Today (local YYYY-MM-DD) — used to hide ongoing cycles whose window
    // hasn't fully closed yet. Mirrors Sales Reconciliation behaviour so
    // weekly/monthly purchase cycles only appear on the day AFTER they end
    // (e.g. a cycle 2026-05-25 → 2026-05-31 shows up on 2026-06-01).
    const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

    // Two-state view: Pending (everything not yet posted) vs Posted.
    // The "Reconciled" intermediate tab was removed — reconcile + post is a
    // single user action, so reconciled / disputed cycles stay in Pending
    // until they get posted (then they move to Posted) or skipped (then
    // they move to Skipped).
    // Pending also hides ongoing cycles (cycle_end >= today).
    const pendingCyclesRaw = useMemo(() => filteredCycles.filter((c) => c.status !== "posted"), [filteredCycles]);
    const pendingCycles    = useMemo(() => pendingCyclesRaw.filter((c) => (c.cycle_end || "") < today), [pendingCyclesRaw, today]);
    const pendingOngoingHidden = pendingCyclesRaw.length - pendingCycles.length;
    const postedCycles     = useMemo(() => filteredCycles.filter((c) => c.status === "posted"), [filteredCycles]);

    return (
        <div data-testid="purchase-invoices-page">
            <PageHeader
                title="Purchase Invoices"
                eyebrow="Procurement / Reconciliation"
                description="Daily server-data → cycle aggregation → vendor-invoice reconciliation."
                action={canWrite ? (
                    <div className="flex items-center gap-2 flex-wrap">
                        <RefreshPurchaseButton onDone={load}/>
                        <ManualInvoiceButton onDone={load}/>
                        <RescaleLegacyButton onDone={load}/>
                        <DailyUploadButton onDone={load}/>
                    </div>
                ) : null}
            />

            <div className="flex flex-wrap items-center gap-3 mb-3">
                <PeriodFilter initialPreset="this_month" onChange={setPeriod}/>
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by partner name…"
                        className="rounded-sm h-9 pl-7 w-64"
                        data-testid="purchase-search-input"
                    />
                    {query && (
                        <button type="button" onClick={() => setQuery("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-[12px]"
                                aria-label="Clear search">×</button>
                    )}
                </div>
                {loading && <span className="text-[12px] text-slate-500">Loading…</span>}
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="rounded-sm bg-slate-100">
                    <TabsTrigger value="recon" data-testid="purchase-tab-recon" className="rounded-sm">
                        Pending ({pendingCycles.length})
                    </TabsTrigger>
                    <TabsTrigger value="posted" data-testid="purchase-tab-posted" className="rounded-sm">
                        Posted ({postedCycles.length})
                    </TabsTrigger>
                    <TabsTrigger value="skipped" data-testid="purchase-tab-skipped" className="rounded-sm">
                        Skipped ({filteredSkipped.length}{query && filteredSkipped.length !== skippedCycles.length ? ` of ${skippedCycles.length}` : ""})
                    </TabsTrigger>
                    <TabsTrigger value="uploads" data-testid="purchase-tab-uploads" className="rounded-sm">Daily Uploads ({uploads.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="recon" className="mt-3">
                    {pendingOngoingHidden > 0 && (
                        <div className="mb-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm inline-block"
                             data-testid="purchase-pending-ongoing-hint"
                             title="Cycles whose end date is today or later are hidden — they'll appear once the cycle window has fully closed.">
                            {pendingOngoingHidden} ongoing cycle{pendingOngoingHidden === 1 ? "" : "s"} hidden (window not yet closed)
                        </div>
                    )}
                    <CyclesTable cycles={pendingCycles} entities={entities} canWrite={canWrite} onChanged={load}/>
                </TabsContent>
                <TabsContent value="posted" className="mt-3">
                    <div className="text-[11.5px] text-slate-500 mb-2">Final posted invoices. View only — posting cannot be reversed from this screen.</div>
                    <CyclesTable cycles={postedCycles} entities={entities} canWrite={canWrite} onChanged={load}/>
                </TabsContent>
                <TabsContent value="skipped" className="mt-3">
                    <div className="text-[11.5px] text-slate-500 mb-2">Skipped cycles are hidden from the Pending tab. Click <b>Unskip</b> to restore them to Pending, or use the checkboxes to bulk-delete.</div>
                    <CyclesTable cycles={filteredSkipped} entities={entities} canWrite={canWrite} onChanged={load} bulkSelectable={canWrite}/>
                </TabsContent>
                <TabsContent value="uploads" className="mt-3">
                    <UploadsTable uploads={uploads} canWrite={canWrite} onChanged={load}/>
                </TabsContent>
            </Tabs>
        </div>
    );
}

// Try to infer a YYYY-MM-DD date from a filename. Supports:
//   20250427, 2025-04-27, 2025_04_27, 27-04-2025, 27_04_2025, 27Apr2025, 27-Apr-2025
const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };
const pad = (n) => String(n).padStart(2, "0");
const valid = (y, m, d) => y >= 2000 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31;
function inferDateFromFilename(name) {
    const base = (name || "").replace(/\.[^.]+$/, "");
    // ISO-ish: 2025-04-27 / 2025_04_27 / 20250427
    let m = base.match(/(20\d{2})[-_/]?(\d{2})[-_/]?(\d{2})/);
    if (m) {
        const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
        if (valid(y, mo, d)) return `${y}-${pad(mo)}-${pad(d)}`;
    }
    // DD-MM-YYYY / DD_MM_YYYY / DD/MM/YYYY
    m = base.match(/(\d{1,2})[-_/](\d{1,2})[-_/](20\d{2})/);
    if (m) {
        const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
        if (valid(y, mo, d)) return `${y}-${pad(mo)}-${pad(d)}`;
    }
    // 27Apr2025 / 27-Apr-2025 / 27 Apr 2025
    m = base.match(/(\d{1,2})[-_\s]?([A-Za-z]{3,4})[-_\s]?(20\d{2})/);
    if (m) {
        const mo = MONTHS[m[2].toLowerCase()];
        const [d, y] = [Number(m[1]), Number(m[3])];
        if (mo && valid(y, mo, d)) return `${y}-${pad(mo)}-${pad(d)}`;
    }
    return "";
}

function RefreshPurchaseButton({ onDone }) {
    const [busy, setBusy] = useState(false);
    const click = async () => {
        const msg = "Refresh all pending purchase cycles?\n\n• Re-applies the current server-name mapping to every uploaded row\n• Rebuilds every pending cycle using the current partner-master config\n• Posted, skipped, and manual invoices are preserved untouched\n\nUse this after creating/updating partner masters or server-name mappings.";
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/purchase/refresh-cycles");
            toast.success(`Refreshed — ${data.remapped_uploads} uploads re-mapped, ${data.pending_cycles_rebuilt} pending cycles rebuilt (${data.posted_preserved} posted + ${data.skipped_preserved} skipped preserved)`);
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Refresh failed");
        } finally { setBusy(false); }
    };
    return (
        <Button variant="outline" className="rounded-sm" onClick={click} disabled={busy} data-testid="purchase-refresh-btn" title="Re-apply server-name mapping & rebuild cycles using current partner master config">
            <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`}/>{busy ? "Refreshing…" : "Refresh"}
        </Button>
    );
}

function ManualInvoiceButton({ onDone }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const today = () => new Date().toISOString().slice(0, 10);
    const blank = () => ({
        partner_name: "",
        segment: "Voice",
        cycle_start: today(),
        cycle_end: today(),
        vendor_invoice_number: "",
        vendor_invoice_date: today(),
        vendor_volume: "",
        vendor_amount: "",
        currency: "",
        remarks: "",
    });
    const [form, setForm] = useState(blank());
    const [file, setFile] = useState(null);
    // Auto-preview of "our" server figures for the chosen partner/segment/period.
    const [preview, setPreview] = useState(null);  // { our_volume, our_amount, days_covered: [...] }
    const [previewing, setPreviewing] = useState(false);

    const reset = () => { setForm(blank()); setFile(null); setPreview(null); };

    // Whenever partner / segment / period change, fetch the aggregated server
    // figures so the user sees "Our server data: X minutes / Y USD" upfront
    // and can compare to what the vendor is invoicing. Debounced via the
    // partner-name trim to avoid a roundtrip per keystroke.
    useEffect(() => {
        const name = form.partner_name.trim();
        if (!name || !form.cycle_start || !form.cycle_end || form.cycle_end < form.cycle_start) {
            setPreview(null);
            return;
        }
        let cancelled = false;
        setPreviewing(true);
        const handle = setTimeout(async () => {
            try {
                const { data } = await api.get("/purchase/manual-invoice/preview", {
                    params: {
                        partner_name: name,
                        segment: form.segment,
                        from: form.cycle_start,
                        to: form.cycle_end,
                    },
                });
                if (!cancelled) setPreview(data);
            } catch (e) {
                if (!cancelled) setPreview(null);
            } finally {
                if (!cancelled) setPreviewing(false);
            }
        }, 400);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [form.partner_name, form.segment, form.cycle_start, form.cycle_end]);

    // Diff computation against the user's typed vendor values.
    const diff = useMemo(() => {
        if (!preview) return null;
        const vv = parseFloat(form.vendor_volume);
        const va = parseFloat(form.vendor_amount);
        const out = {};
        if (!isNaN(vv) && preview.our_volume) {
            out.volume_pct = ((vv - preview.our_volume) / preview.our_volume) * 100;
            out.volume_abs = vv - preview.our_volume;
        }
        if (!isNaN(va) && preview.our_amount) {
            out.amount_pct = ((va - preview.our_amount) / preview.our_amount) * 100;
            out.amount_abs = va - preview.our_amount;
        }
        return out;
    }, [preview, form.vendor_volume, form.vendor_amount]);

    // Colour by magnitude — within 1% green, within 5% amber, else red. Same
    // threshold the auto-recon flow uses (>1% needs dispute Y/N).
    const diffClass = (pct) => {
        if (pct === undefined || pct === null || isNaN(pct)) return "text-slate-500";
        const a = Math.abs(pct);
        if (a < 1) return "text-emerald-700";
        if (a < 5) return "text-amber-700";
        return "text-red-700";
    };
    const fmt = (n, d = 2) => (n === null || n === undefined || isNaN(n))
        ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

    const submit = async () => {
        if (!form.partner_name.trim()) return toast.error("Partner is required");
        const amt = parseFloat(form.vendor_amount);
        if (!amt || amt < 0) return toast.error("Vendor amount is required (≥ 0)");
        if (form.cycle_end < form.cycle_start) return toast.error("Cycle end must be ≥ cycle start");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("partner_name", form.partner_name.trim());
            fd.append("segment", form.segment);
            fd.append("cycle_start", form.cycle_start);
            fd.append("cycle_end", form.cycle_end);
            fd.append("vendor_amount", amt.toString());
            if (form.vendor_volume) fd.append("vendor_volume", String(parseFloat(form.vendor_volume)));
            if (form.vendor_invoice_number) fd.append("vendor_invoice_number", form.vendor_invoice_number.trim());
            if (form.vendor_invoice_date) fd.append("vendor_invoice_date", form.vendor_invoice_date);
            if (form.currency) fd.append("currency", form.currency.toUpperCase().trim());
            if (form.remarks) fd.append("remarks", form.remarks);
            if (file) fd.append("file", file);
            await api.post("/purchase/manual-invoice", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Manual invoice posted for ${form.partner_name}`);
            setOpen(false);
            reset();
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to post manual invoice");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="purchase-manual-btn">
                    <FilePlus2 size={14} className="mr-1.5"/>Manual Invoice
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-2xl" data-testid="purchase-manual-dialog">
                <DialogHeader>
                    <DialogTitle>Enter Vendor Invoice Manually</DialogTitle>
                </DialogHeader>
                <div className="text-[11.5px] text-slate-500 -mt-2 mb-2">
                    For partners with daily server-data feeds, our figures are auto-aggregated for the chosen period — you'll see the diff against the vendor's invoice before posting. For one-off vendors with no feed, leave the diff at "—" and the row still posts as a vanilla expense.
                </div>

                {/* Server-data preview band — visible once partner + period are valid. */}
                {(previewing || preview) && (
                    <div className="border border-slate-200 bg-slate-50 rounded-sm px-3 py-2 mb-3 text-[12.5px]" data-testid="purchase-manual-preview-band">
                        {previewing ? (
                            <span className="text-slate-500 inline-flex items-center"><RefreshCw size={12} className="mr-1.5 animate-spin"/>Fetching server data…</span>
                        ) : preview && (preview.our_volume > 0 || preview.our_amount > 0) ? (
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                                <span className="text-[10px] uppercase tracking-widest text-slate-500">Our server data</span>
                                <span className="font-mono">
                                    <span className="text-slate-500">vol:</span> {fmt(preview.our_volume, form.segment === "Voice" ? 2 : 0)}
                                </span>
                                <span className="font-mono">
                                    <span className="text-slate-500">amt:</span> {fmt(preview.our_amount)} {form.segment === "Voice" ? "USD" : "EUR"}
                                </span>
                                <span className="text-slate-400 text-[11px]">{preview.days_covered.length} day{preview.days_covered.length === 1 ? "" : "s"} covered</span>
                                {diff && (diff.volume_pct !== undefined || diff.amount_pct !== undefined) && (
                                    <span className="ml-auto inline-flex gap-3">
                                        {diff.volume_pct !== undefined && (
                                            <span className={`font-mono font-semibold ${diffClass(diff.volume_pct)}`} title="Vendor vs our volume">
                                                Δvol {fmt(diff.volume_pct, 2)}%
                                            </span>
                                        )}
                                        {diff.amount_pct !== undefined && (
                                            <span className={`font-mono font-semibold ${diffClass(diff.amount_pct)}`} title="Vendor vs our amount">
                                                Δamt {fmt(diff.amount_pct, 2)}%
                                            </span>
                                        )}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <span className="text-slate-500">No server data found for this partner / period. The invoice will still post — diff stays blank.</span>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Partner / Vendor">
                        <PartnerAutocomplete
                            value={form.partner_name}
                            onChange={(v) => setForm({ ...form, partner_name: v })}
                            data-testid="purchase-manual-partner"
                            placeholder="Start typing partner name…"
                        />
                    </Field>
                    <Field label="Segment">
                        <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                            <SelectTrigger className="rounded-sm h-9" data-testid="purchase-manual-segment"><SelectValue/></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Voice">Voice (USD base)</SelectItem>
                                <SelectItem value="SMS">SMS (EUR base)</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Service period — from">
                        <Input type="date" value={form.cycle_start} onChange={(e) => setForm({ ...form, cycle_start: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="purchase-manual-from"/>
                    </Field>
                    <Field label="Service period — to">
                        <Input type="date" value={form.cycle_end} onChange={(e) => setForm({ ...form, cycle_end: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="purchase-manual-to"/>
                    </Field>
                    <Field label="Vendor invoice #">
                        <Input value={form.vendor_invoice_number} onChange={(e) => setForm({ ...form, vendor_invoice_number: e.target.value })} className="rounded-sm h-9 font-mono" placeholder="e.g. INV-2026-00123" data-testid="purchase-manual-vinum"/>
                    </Field>
                    <Field label="Vendor invoice date">
                        <Input type="date" value={form.vendor_invoice_date} onChange={(e) => setForm({ ...form, vendor_invoice_date: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="purchase-manual-vdate"/>
                    </Field>
                    <Field label="Vendor volume (optional)">
                        <Input type="number" step="0.0001" value={form.vendor_volume} onChange={(e) => setForm({ ...form, vendor_volume: e.target.value })} className="rounded-sm h-9 text-right font-mono" placeholder={form.segment === "Voice" ? "minutes" : "messages"} data-testid="purchase-manual-vol"/>
                    </Field>
                    <Field label={`Vendor amount${form.currency ? ` (${form.currency.toUpperCase()})` : (form.segment === "Voice" ? " (USD)" : " (EUR)")}`}>
                        <Input type="number" step="0.0001" value={form.vendor_amount} onChange={(e) => setForm({ ...form, vendor_amount: e.target.value })} className="rounded-sm h-9 text-right font-mono" placeholder="0.00" data-testid="purchase-manual-amt"/>
                    </Field>
                    <Field label="Currency (leave blank for segment base)">
                        <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="rounded-sm h-9 font-mono" placeholder={form.segment === "Voice" ? "USD" : "EUR"} maxLength={4}/>
                    </Field>
                    <Field label="Invoice copy (optional)">
                        <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-sm h-9 file:text-[12px] file:rounded-sm file:border-0 file:bg-slate-100 file:px-2 file:py-1 file:mr-2" data-testid="purchase-manual-file"/>
                    </Field>
                </div>
                <Field label="Remarks (optional)">
                    <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="rounded-sm" placeholder="Notes for this invoice" rows={2}/>
                </Field>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="purchase-manual-submit">
                        {busy ? "Posting…" : "Post Invoice"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function RescaleLegacyButton({ onDone }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [factor, setFactor] = useState("60");
    const submit = async () => {
        const f = parseFloat(factor);
        if (!f || f <= 0) return toast.error("Factor must be > 0");
        setBusy(true);
        try {
            const r = await api.post(`/purchase/rescale-legacy-volumes?factor=${f}`);
            toast.success(`Rescaled ${r.data.voice_uploads_rescaled} uploads & ${r.data.voice_cycles_rescaled} cycles by ÷${f}`);
            setOpen(false);
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Rescale failed");
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-sm h-8 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="rescale-legacy-btn">
                    <AlertTriangle size={13} className="mr-1.5"/>Fix legacy volumes
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-md" data-testid="rescale-dialog">
                <DialogHeader><DialogTitle>Rescale Voice volumes (one-shot)</DialogTitle></DialogHeader>
                <div className="space-y-3 text-[12.5px] leading-relaxed">
                    <p className="text-slate-600">
                        Use this if your earlier Voice uploads show inflated minutes (e.g. <span className="font-mono">11,53,584 min</span> instead of <span className="font-mono">19,226 min</span>). Before <b>2026-05-01</b>, the parser misread the vendor’s <span className="font-mono">MM:SS</span> format as <span className="font-mono">HH:MM</span>, inflating every Voice volume by ~60×.
                    </p>
                    <p className="text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
                        <b>Run once.</b> Running twice will under-scale. Posted cycles and SMS uploads are left untouched.
                    </p>
                    <Field label="Divide every Voice volume by">
                        <Input value={factor} onChange={(e) => setFactor(e.target.value)} className="rounded-sm h-9 font-mono" data-testid="rescale-factor-input"/>
                    </Field>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-sm bg-amber-600 hover:bg-amber-700" data-testid="rescale-submit">
                        {busy ? "Rescaling…" : "Rescale now"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function DailyUploadButton({ onDone }) {
    const [open, setOpen] = useState(false);
    const [segment, setSegment] = useState("Voice");
    const [items, setItems] = useState([]); // [{ id, file, date, status, error }]
    const [busy, setBusy] = useState(false);

    const reset = () => { setItems([]); setBusy(false); };

    const onPick = (e) => {
        const picked = Array.from(e.target.files || []);
        const next = picked.map((f, i) => ({
            id: `${Date.now()}-${i}-${f.name}`,
            file: f,
            date: inferDateFromFilename(f.name) || new Date().toISOString().slice(0, 10),
            status: "pending",
            error: "",
        }));
        setItems((prev) => [...prev, ...next]);
        e.target.value = "";
    };

    const updateDate = (id, date) => setItems((prev) => prev.map((it) => it.id === id ? { ...it, date } : it));
    const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

    const submitAll = async () => {
        if (!items.length) return toast.error("Select at least one file");
        const missing = items.filter((it) => !it.date);
        if (missing.length) return toast.error(`${missing.length} file(s) missing a date`);
        // Detect duplicate (segment, date) within this batch
        const seen = new Set();
        for (const it of items) {
            const k = `${segment}|${it.date}`;
            if (seen.has(k)) return toast.error(`Duplicate date ${it.date} in batch — keep only one file per date`);
            seen.add(k);
        }
        setBusy(true);
        let ok = 0, fail = 0;
        for (const it of items) {
            setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "uploading", error: "" } : x));
            try {
                const fd = new FormData();
                fd.append("segment", segment);
                fd.append("date", it.date);
                fd.append("file", it.file);
                await api.post("/purchase/daily-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "done" } : x));
                ok += 1;
            } catch (e) {
                const msg = e.response?.data?.detail || "Upload failed";
                setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "error", error: msg } : x));
                fail += 1;
            }
        }
        setBusy(false);
        if (ok) toast.success(`Uploaded ${ok} file(s) & cycles updated${fail ? ` — ${fail} failed` : ""}`);
        else toast.error(`All ${fail} upload(s) failed`);
        onDone?.();
        if (!fail) { setOpen(false); reset(); }
    };

    const statusBadge = (s) => {
        if (s === "done") return <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">Done</span>;
        if (s === "uploading") return <span className="text-[10px] uppercase tracking-widest text-[#002FA7] bg-blue-50 border border-blue-200 px-1.5 py-0.5">Uploading…</span>;
        if (s === "error") return <span className="text-[10px] uppercase tracking-widest text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5">Error</span>;
        return <span className="text-[10px] uppercase tracking-widest text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5">Pending</span>;
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="purchase-upload-btn">
                    <Upload size={14} className="mr-1.5"/>Upload daily data
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-3xl" data-testid="purchase-upload-dialog">
                <DialogHeader><DialogTitle>Upload daily server data</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Segment (applies to all files)">
                        <Select value={segment} onValueChange={setSegment}>
                            <SelectTrigger className="rounded-sm" data-testid="purchase-upload-segment"><SelectValue/></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Voice">Voice (USD base)</SelectItem>
                                <SelectItem value="SMS">SMS (EUR base)</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Pick files (multi-select)">
                        <Input type="file" accept=".csv,.xlsx" multiple onChange={onPick} className="rounded-sm h-9" data-testid="purchase-upload-file"/>
                    </Field>
                </div>

                {items.length > 0 && (
                    <div className="mt-3 border border-slate-200 max-h-[320px] overflow-y-auto" data-testid="purchase-upload-queue">
                        <table className="w-full text-[12.5px] financial">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                                    <th className="px-2 py-2">File</th>
                                    <th className="px-2 py-2 w-40">Data date</th>
                                    <th className="px-2 py-2 w-24">Status</th>
                                    <th className="px-2 py-2 w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => (
                                    <tr key={it.id} className="border-b border-slate-100" data-testid={`purchase-upload-item-${it.id}`}>
                                        <td className="px-2 py-1.5 truncate max-w-[240px]" title={it.file.name}>{it.file.name}</td>
                                        <td className="px-2 py-1.5">
                                            <Input type="date" value={it.date} onChange={(e) => updateDate(it.id, e.target.value)} disabled={busy || it.status === "done"} className="rounded-sm h-8 font-mono text-[12px]" data-testid={`purchase-upload-date-${it.id}`}/>
                                        </td>
                                        <td className="px-2 py-1.5">
                                            {statusBadge(it.status)}
                                            {it.error && <div className="text-[10.5px] text-red-700 mt-0.5 truncate max-w-[180px]" title={it.error}>{it.error}</div>}
                                        </td>
                                        <td className="px-2 py-1.5">
                                            {!busy && it.status !== "done" && (
                                                <Button variant="ghost" size="sm" onClick={() => removeItem(it.id)} className="h-7 px-2 text-red-600" data-testid={`purchase-upload-remove-${it.id}`}><Trash2 size={12}/></Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="text-[11.5px] text-slate-500 mt-2 leading-relaxed">
                    Select multiple files at once — dates are auto-detected from filenames (e.g. <span className="font-mono">2025-04-27</span>, <span className="font-mono">27Apr2025</span>). Edit any date before uploading.
                    Files are uploaded sequentially; re-uploading the same (segment, date) replaces the previous file.
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => { setOpen(false); reset(); }} disabled={busy}>Close</Button>
                    <Button onClick={submitAll} disabled={busy || items.length === 0} className="rounded-sm bg-[#002FA7]" data-testid="purchase-upload-submit">
                        {busy ? "Uploading…" : `Upload ${items.length || ""} file${items.length === 1 ? "" : "s"}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function UploadsTable({ uploads, canWrite, onChanged }) {
    const remove = async (id) => {
        if (!window.confirm("Delete this daily upload? Cycles will be recomputed.")) return;
        try {
            await api.delete(`/purchase/daily-uploads/${id}`);
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };
    return (
        <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="purchase-uploads-table">
            <table className="w-full text-[13px] financial min-w-[800px]">
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                        <th className="px-3 py-2.5">Date</th>
                        <th className="px-3 py-2.5">Segment</th>
                        <th className="px-3 py-2.5">File</th>
                        <th className="px-3 py-2.5 num">Partners</th>
                        <th className="px-3 py-2.5 num">Total Volume</th>
                        <th className="px-3 py-2.5 num">Total Amount</th>
                        <th className="px-3 py-2.5 w-12"></th>
                    </tr>
                </thead>
                <tbody>
                    {uploads.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-10">No daily uploads in this period</td></tr>}
                    {uploads.map((u) => (
                        <tr key={u.id} className="border-b border-slate-100" data-testid={`purchase-upload-row-${u.id}`}>
                            <td className="px-3 py-2 font-mono">{u.date}</td>
                            <td className="px-3 py-2"><SegBadge segment={u.segment}/></td>
                            <td className="px-3 py-2 text-slate-600 truncate max-w-[260px]">{u.filename}</td>
                            <td className="px-3 py-2 num">{(u.rows || []).length}</td>
                            <td className="px-3 py-2 num">{fmtVol(u.total_volume, u.segment)}</td>
                            <td className="px-3 py-2 num font-semibold">{num(u.total_amount)}</td>
                            <td className="px-3 py-2">
                                {canWrite && <Button variant="ghost" size="sm" onClick={() => remove(u.id)} className="h-7 px-2 text-red-600" data-testid={`purchase-upload-delete-${u.id}`}><Trash2 size={13}/></Button>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CyclesTable({ cycles, entities, canWrite, onChanged, bulkSelectable }) {
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    // Prune selections that fall outside the currently rendered rows (e.g. after
    // a refresh, tab switch, or query filter change).
    useEffect(() => {
        if (!bulkSelectable) return;
        const visible = new Set(cycles.map((c) => c.id));
        setSelectedIds((prev) => {
            const next = new Set([...prev].filter((id) => visible.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [cycles, bulkSelectable]);

    const toggleOne = (id) => setSelectedIds((p) => {
        const n = new Set(p);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });
    const toggleAll = () => setSelectedIds((p) => {
        const all = cycles.length > 0 && cycles.every((c) => p.has(c.id));
        return all ? new Set() : new Set(cycles.map((c) => c.id));
    });
    const allSelected = cycles.length > 0 && cycles.every((c) => selectedIds.has(c.id));

    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (!ids.length) return;
        if (!window.confirm(`Permanently delete ${ids.length} skipped cycle${ids.length === 1 ? "" : "s"}?\n\nThis cannot be undone.`)) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/purchase/cycles/bulk-delete", { ids });
            const parts = [`Deleted ${data.deleted}`];
            if (data.blocked?.length) parts.push(`${data.blocked.length} blocked (posted)`);
            if (data.missing?.length) parts.push(`${data.missing.length} missing`);
            toast.success(parts.join(" — "));
            setSelectedIds(new Set());
            onChanged && onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk delete failed");
        } finally { setBulkBusy(false); }
    };

    return (
        <div data-testid="purchase-cycles-table">
            {bulkSelectable && selectedIds.size > 0 && (
                <div className="flex items-center justify-between gap-3 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-sm" data-testid="purchase-bulk-bar">
                    <div className="text-[12.5px] text-red-700">
                        <strong>{selectedIds.size}</strong> cycle{selectedIds.size === 1 ? "" : "s"} selected
                    </div>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" className="h-8 rounded-sm text-slate-600" onClick={() => setSelectedIds(new Set())} data-testid="purchase-bulk-clear">Clear</Button>
                        <Button size="sm" className="h-8 rounded-sm bg-red-600 hover:bg-red-700" onClick={bulkDelete} disabled={bulkBusy} data-testid="purchase-bulk-delete-btn">
                            <Trash2 size={13} className="mr-1.5"/>{bulkBusy ? "Deleting…" : `Delete ${selectedIds.size}`}
                        </Button>
                    </div>
                </div>
            )}
            <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-[12.5px] financial min-w-[1180px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            {bulkSelectable && (
                                <th className="px-2 py-2 w-9">
                                    <Checkbox checked={allSelected} onCheckedChange={toggleAll}
                                              data-testid="purchase-bulk-header-checkbox"
                                              aria-label="Select all skipped cycles"/>
                                </th>
                            )}
                            <th className="px-2 py-2">Cycle</th>
                            <th className="px-2 py-2">Seg</th>
                            <th className="px-2 py-2">Partner</th>
                            <th className="px-2 py-2 num">Our Vol</th>
                            <th className="px-2 py-2 num">Our Amt</th>
                            <th className="px-2 py-2 num">Vendor Vol</th>
                            <th className="px-2 py-2 num">Vendor Amt</th>
                            <th className="px-2 py-2 num">Vol Diff</th>
                            <th className="px-2 py-2 num">Amt Diff</th>
                            <th className="px-2 py-2">Invoice</th>
                            <th className="px-2 py-2">Dispute</th>
                            <th className="px-2 py-2">Status</th>
                            <th className="px-2 py-2 w-32"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {cycles.length === 0 && <tr><td colSpan={bulkSelectable ? 14 : 13} className="text-center text-slate-500 py-10">No cycles in this period.</td></tr>}
                        {cycles.map((c) => (
                            <CycleRow key={c.id} c={c} entities={entities} canWrite={canWrite} onChanged={onChanged}
                                      selectable={bulkSelectable}
                                      selected={selectedIds.has(c.id)}
                                      onToggleSelect={() => toggleOne(c.id)}/>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function pctClass(p) {
    if (p === null || p === undefined) return "text-slate-400";
    return Math.abs(p) > 1 ? "text-red-600 font-semibold" : "text-emerald-700";
}
function fmtPct(p) {
    if (p === null || p === undefined) return "—";
    const sign = p > 0 ? "+" : "";
    return `${sign}${num(p, 2)}%`;
}

function CycleRow({ c, entities, canWrite, onChanged, selectable, selected, onToggleSelect }) {
    const [editing, setEditing] = useState(false);
    const [busy, setBusy] = useState(false);
    const ovOverride = c.our_volume_override !== undefined && c.our_volume_override !== null;
    const oaOverride = c.our_amount_override !== undefined && c.our_amount_override !== null;
    const ovEff = ovOverride ? c.our_volume_override : c.our_volume;
    const oaEff = oaOverride ? c.our_amount_override : c.our_amount;

    const skip = async () => {
        if (!window.confirm(`Skip ${c.partner_name} for ${c.cycle_start} → ${c.cycle_end}?\n\nIt will be hidden from the reconciliation list. Use "Show skipped only" to restore.`)) return;
        setBusy(true);
        try {
            await api.post(`/purchase/cycles/${c.id}/skip`);
            toast.success(`Skipped ${c.partner_name}`);
            onChanged && onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Skip failed");
        } finally { setBusy(false); }
    };
    const unskip = async () => {
        setBusy(true);
        try {
            await api.post(`/purchase/cycles/${c.id}/unskip`);
            toast.success(`Restored ${c.partner_name}`);
            onChanged && onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Unskip failed");
        } finally { setBusy(false); }
    };
    return (
        <>
            <tr className={`border-b border-slate-100 ${selected ? "bg-blue-50/40" : ((Math.abs(c.volume_diff_pct || 0) > 1 || Math.abs(c.amount_diff_pct || 0) > 1) ? "bg-red-50/50" : "")}`}
                data-testid={`purchase-cycle-row-${c.id}`}>
                {selectable && (
                    <td className="px-2 py-1.5 w-9">
                        <Checkbox checked={!!selected} onCheckedChange={onToggleSelect}
                                  data-testid={`purchase-cycle-select-${c.id}`}
                                  aria-label={`Select cycle ${c.partner_name} ${c.cycle_start}`}/>
                    </td>
                )}
                <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                    <div className="text-[11.5px]">{c.cycle_start} → {c.cycle_end}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest">{c.cycle_type}</div>
                </td>
                <td className="px-2 py-1.5"><SegBadge segment={c.segment}/></td>
                <td className="px-2 py-1.5">{c.partner_name}</td>
                <td className="px-2 py-1.5 num">
                    {fmtVol(ovEff, c.segment)}
                    {ovOverride && <div className="text-[9.5px] text-amber-700 uppercase tracking-widest" title={`Server value: ${fmtVol(c.our_volume, c.segment)}`}>manual · was {fmtVol(c.our_volume, c.segment)}</div>}
                </td>
                <td className="px-2 py-1.5 num font-semibold">
                    {num(oaEff)}
                    {oaOverride && <div className="text-[9.5px] text-amber-700 uppercase tracking-widest font-normal" title={`Server value: ${num(c.our_amount)}`}>manual · was {num(c.our_amount)}</div>}
                </td>
                <td className="px-2 py-1.5 num">{c.vendor_volume === null || c.vendor_volume === undefined ? "—" : fmtVol(c.vendor_volume, c.segment)}</td>
                <td className="px-2 py-1.5 num">{c.vendor_amount === null || c.vendor_amount === undefined ? "—" : num(c.vendor_amount)}</td>
                <td className={`px-2 py-1.5 num ${pctClass(c.volume_diff_pct)}`}>{fmtPct(c.volume_diff_pct)}</td>
                <td className={`px-2 py-1.5 num ${pctClass(c.amount_diff_pct)}`}>{fmtPct(c.amount_diff_pct)}</td>
                <td className="px-2 py-1.5">
                    {c.vendor_invoice_filename
                        ? <a className="text-[#002FA7] underline text-[11.5px]" href={`${API}/purchase/cycles/${c.id}/invoice`} target="_blank" rel="noreferrer" data-testid={`purchase-invoice-link-${c.id}`}><FileText size={11} className="inline mr-0.5"/>view</a>
                        : <span className="text-slate-400 text-[11px]">—</span>}
                </td>
                <td className="px-2 py-1.5">
                    {c.dispute_raised === true && <span className="text-[10px] uppercase tracking-widest text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5">Yes</span>}
                    {c.dispute_raised === false && <span className="text-[10px] uppercase tracking-widest text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5">No</span>}
                    {c.dispute_raised === null && (Math.abs(c.volume_diff_pct || 0) > 1 || Math.abs(c.amount_diff_pct || 0) > 1) && (
                        <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5"><AlertTriangle size={9} className="inline mr-0.5"/>required</span>
                    )}
                </td>
                <td className="px-2 py-1.5">
                    <StatusBadge cycle={c}/>
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                    {canWrite && c.status !== "posted" && c.status !== "skipped" && (
                        <>
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditing(true)} data-testid={`purchase-cycle-edit-${c.id}`}>{c.status === "reconciled" || c.status === "disputed" ? "Edit" : "Reconcile"}</Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-600" onClick={skip} disabled={busy} data-testid={`purchase-cycle-skip-${c.id}`} title="Hide this row from the list (reversible)">Skip</Button>
                        </>
                    )}
                    {canWrite && c.status === "skipped" && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[#002FA7]" onClick={unskip} disabled={busy} data-testid={`purchase-cycle-unskip-${c.id}`} title="Restore to Pending so you can reconcile or post">Unskip</Button>
                    )}
                    {c.status === "posted" && <CheckCircle2 size={14} className="text-emerald-600 inline"/>}
                </td>
            </tr>
            {editing && <ReconcileDialog cycle={c} entities={entities} onClose={() => setEditing(false)} onChanged={onChanged}/>}
        </>
    );
}

function StatusBadge({ cycle }) {
    const map = {
        pending_recon: { label: "Pending recon", cls: "text-slate-600 bg-slate-50 border-slate-200" },
        needs_dispute: { label: "Needs dispute Y/N", cls: "text-amber-700 bg-amber-50 border-amber-200" },
        reconciled: { label: "Reconciled", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
        disputed: { label: "Disputed", cls: "text-red-700 bg-red-50 border-red-200" },
        posted: { label: "Posted", cls: "text-[#002FA7] bg-blue-50 border-blue-200" },
        skipped: { label: "Skipped", cls: "text-slate-500 bg-slate-100 border-slate-300" },
    };
    const info = map[cycle.status] || map.pending_recon;
    return <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${info.cls}`}>{info.label}</span>;
}

function ReconcileDialog({ cycle, entities, onClose, onChanged }) {
    const [vendorVol, setVendorVol] = useState(cycle.vendor_volume ?? "");
    const [vendorAmt, setVendorAmt] = useState(cycle.vendor_amount ?? "");
    const [ourVolOverride, setOurVolOverride] = useState(cycle.our_volume_override ?? "");
    const [ourAmtOverride, setOurAmtOverride] = useState(cycle.our_amount_override ?? "");
    const [dispute, setDispute] = useState(cycle.dispute_raised === null ? "" : cycle.dispute_raised ? "yes" : "no");
    const [notes, setNotes] = useState(cycle.dispute_notes || "");
    const [busy, setBusy] = useState(false);
    const [postOpen, setPostOpen] = useState(false);

    const dataMissing = !cycle.data_complete;

    // Effective "our" values used for diffs — manual override beats server.
    const compute = (vv, va) => {
        const ov = ourVolOverride === "" || ourVolOverride === null
            ? Number(cycle.our_volume) || 0
            : Number(ourVolOverride) || 0;
        const oa = ourAmtOverride === "" || ourAmtOverride === null
            ? Number(cycle.our_amount) || 0
            : Number(ourAmtOverride) || 0;
        const vNum = vv === "" || vv === null ? null : Number(vv);
        const aNum = va === "" || va === null ? null : Number(va);
        return {
            vol: vNum !== null && ov ? ((vNum - ov) / ov) * 100 : null,
            amt: aNum !== null && oa ? ((aNum - oa) / oa) * 100 : null,
        };
    };
    const live = compute(vendorVol, vendorAmt);
    const isOver = (live.vol !== null && Math.abs(live.vol) > 1) || (live.amt !== null && Math.abs(live.amt) > 1);

    const save = async (afterSave) => {
        if (dataMissing) return toast.error(`Daily data missing for ${cycle.missing_dates?.length} day(s) — upload first`);
        if (vendorVol === "" || vendorAmt === "") return toast.error("Vendor volume and amount are required");
        if (isOver && dispute === "") return toast.error("Diff > 1% — please mark dispute Yes/No");
        if (dispute === "yes" && !notes.trim()) return toast.error("Dispute notes are required when dispute = Yes");
        setBusy(true);
        try {
            const body = {
                vendor_volume: Number(vendorVol),
                vendor_amount: Number(vendorAmt),
                dispute_raised: dispute === "" ? null : dispute === "yes",
                dispute_notes: notes,
            };
            // Volume override
            if (ourVolOverride === "" || ourVolOverride === null) {
                if (cycle.our_volume_override !== null && cycle.our_volume_override !== undefined) {
                    body.clear_our_volume_override = true;
                }
            } else {
                body.our_volume_override = Number(ourVolOverride);
            }
            // Amount override
            if (ourAmtOverride === "" || ourAmtOverride === null) {
                if (cycle.our_amount_override !== null && cycle.our_amount_override !== undefined) {
                    body.clear_our_amount_override = true;
                }
            } else {
                body.our_amount_override = Number(ourAmtOverride);
            }
            await api.put(`/purchase/cycles/${cycle.id}/vendor-data`, body);
            toast.success("Reconciliation saved");
            onChanged?.();
            if (afterSave === "post") setPostOpen(true);
            else onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const onUploadInvoice = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/purchase/cycles/${cycle.id}/upload-invoice`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Vendor invoice uploaded");
            onChanged?.();
        } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    };

    return (
        <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-sm max-w-3xl" data-testid="purchase-recon-dialog">
                <DialogHeader>
                    <DialogTitle>Reconcile — {cycle.partner_name}</DialogTitle>
                </DialogHeader>
                <div className="text-[12px] text-slate-500 -mt-2 mb-2 font-mono">
                    Cycle: {cycle.cycle_start} → {cycle.cycle_end} ({cycle.cycle_type})
                </div>
                {dataMissing && (
                    <div className="border border-amber-200 bg-amber-50 text-amber-800 text-[12px] px-3 py-2 mb-3 rounded-sm">
                        <AlertTriangle size={13} className="inline mr-1.5"/>
                        Daily data missing for {cycle.missing_dates?.length} day(s):
                        <span className="font-mono ml-1">{(cycle.missing_dates || []).slice(0, 6).join(", ")}{(cycle.missing_dates || []).length > 6 ? "…" : ""}</span>.
                        Upload all daily files before reconciling.
                    </div>
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Our (server) data</div>
                            <div className="text-[10px] text-slate-400">manual override allowed</div>
                        </div>
                        <Field label={`Volume ${cycle.segment === "Voice" ? "(minutes)" : "(count)"} — server: ${fmtVol(cycle.our_volume, cycle.segment)}`}>
                            <div className="flex items-center gap-1.5">
                                <Input
                                    type="number" step="0.01"
                                    placeholder={String(cycle.our_volume ?? "")}
                                    value={ourVolOverride}
                                    onChange={(e) => setOurVolOverride(e.target.value)}
                                    className="rounded-sm h-9 font-mono"
                                    data-testid="purchase-recon-our-vol"
                                />
                                {ourVolOverride !== "" && (
                                    <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-slate-500" onClick={() => setOurVolOverride("")} data-testid="purchase-recon-our-vol-clear" title="Revert to server value">↻</Button>
                                )}
                            </div>
                        </Field>
                        <Field label={`Amount — server: ${num(cycle.our_amount)}`}>
                            <div className="flex items-center gap-1.5">
                                <Input
                                    type="number" step="0.01"
                                    placeholder={String(cycle.our_amount ?? "")}
                                    value={ourAmtOverride}
                                    onChange={(e) => setOurAmtOverride(e.target.value)}
                                    className="rounded-sm h-9 font-mono"
                                    data-testid="purchase-recon-our-amt"
                                />
                                {ourAmtOverride !== "" && (
                                    <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-slate-500" onClick={() => setOurAmtOverride("")} data-testid="purchase-recon-our-amt-clear" title="Revert to server value">↻</Button>
                                )}
                            </div>
                        </Field>
                        <div className="text-[10.5px] text-slate-400 mt-1">Leave blank to use the auto-computed server value.</div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Vendor invoice data</div>
                        <Field label={`Vendor volume ${cycle.segment === "Voice" ? "(minutes)" : "(count)"}`}>
                            <Input type="number" step="0.01" value={vendorVol} disabled={dataMissing} onChange={(e) => setVendorVol(e.target.value)} className="rounded-sm h-9 font-mono" data-testid="purchase-recon-vendor-vol"/>
                        </Field>
                        <Field label="Vendor amount">
                            <Input type="number" step="0.01" value={vendorAmt} disabled={dataMissing} onChange={(e) => setVendorAmt(e.target.value)} className="rounded-sm h-9 font-mono" data-testid="purchase-recon-vendor-amt"/>
                        </Field>
                    </div>
                    <div className={`col-span-2 grid grid-cols-2 gap-3 px-3 py-2 ${isOver ? "bg-red-50 border border-red-200" : "bg-emerald-50 border border-emerald-200"} rounded-sm`}>
                        <div className="text-[12.5px]">
                            Volume diff: <span className={`font-mono font-semibold ${live.vol !== null && Math.abs(live.vol) > 1 ? "text-red-700" : "text-emerald-700"}`}>{live.vol === null ? "—" : `${live.vol > 0 ? "+" : ""}${num(live.vol, 2)}%`}</span>
                        </div>
                        <div className="text-[12.5px]">
                            Amount diff: <span className={`font-mono font-semibold ${live.amt !== null && Math.abs(live.amt) > 1 ? "text-red-700" : "text-emerald-700"}`}>{live.amt === null ? "—" : `${live.amt > 0 ? "+" : ""}${num(live.amt, 2)}%`}</span>
                        </div>
                    </div>
                    <Field label="Vendor invoice file">
                        <div className="flex items-center gap-2">
                            <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.xlsx,.csv" onChange={onUploadInvoice} className="rounded-sm h-9" data-testid="purchase-recon-invoice-upload"/>
                            {cycle.vendor_invoice_filename && <a className="text-[#002FA7] underline text-[11.5px]" href={`${API}/purchase/cycles/${cycle.id}/invoice`} target="_blank" rel="noreferrer">view current</a>}
                        </div>
                    </Field>
                    <Field label={`Dispute raised? ${isOver ? "(required)" : "(optional)"}`}>
                        <Select value={dispute} onValueChange={setDispute}>
                            <SelectTrigger className="rounded-sm" data-testid="purchase-recon-dispute"><SelectValue placeholder="Select"/></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="no">No</SelectItem>
                                <SelectItem value="yes">Yes</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <div className="col-span-2">
                        <Field label={`Dispute notes ${dispute === "yes" ? "(required)" : "(optional)"}`}>
                            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" data-testid="purchase-recon-notes"/>
                        </Field>
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button onClick={() => save("close")} disabled={busy || dataMissing} className="rounded-sm" variant="outline" data-testid="purchase-recon-save-btn">{busy ? "Saving…" : "Save"}</Button>
                    <Button onClick={() => save("post")} disabled={busy || dataMissing} className="rounded-sm bg-[#002FA7]" data-testid="purchase-recon-save-post-btn">{busy ? "Saving…" : "Save & Post →"}</Button>
                </DialogFooter>
                {postOpen && <PostDialog cycle={cycle} onClose={() => { setPostOpen(false); onClose(); }} onChanged={onChanged}/>}
            </DialogContent>
        </Dialog>
    );
}

function PostDialog({ cycle, onClose, onChanged }) {
    const [form, setForm] = useState({
        amount_used: "vendor",
        remarks: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/purchase/cycles/${cycle.id}/post`, form);
            toast.success("Purchase cycle posted");
            onChanged?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Post failed");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-sm max-w-lg" data-testid="purchase-post-dialog">
                <DialogHeader><DialogTitle>Post — {cycle.partner_name}</DialogTitle></DialogHeader>
                <div className="text-[12px] text-slate-500 -mt-1 mb-2">
                    Posting marks this cycle as final & locked. The actual cash payment is recorded later via the Payments module against a bank account.
                </div>
                <div className="grid gap-3">
                    <Field label="Final amount to record">
                        <Select value={form.amount_used} onValueChange={(v) => setForm({ ...form, amount_used: v })}>
                            <SelectTrigger className="rounded-sm" data-testid="post-amount-used"><SelectValue/></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="our">Our (server) — {num(cycle.our_amount_override ?? cycle.our_amount)}{cycle.our_amount_override != null ? " (manual)" : ""}</SelectItem>
                                <SelectItem value="vendor">Vendor invoice — {cycle.vendor_amount === null ? "—" : num(cycle.vendor_amount)}</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Remarks (optional)">
                        <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="rounded-sm h-9"/>
                    </Field>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#002FA7]" data-testid="post-submit-btn"><FileUp size={14} className="mr-1.5"/>{busy ? "Posting…" : "Post cycle"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
